import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ISkillMapParams,
  isHistoryPush,
  nextSkillMapSearch,
  readSkillMapParams,
} from '../model/skillMapParams';

/**
 * Состояние карты в адресной строке (ТЗ 0070).
 *
 * Тонкая обёртка над двумя чистыми функциями: вся политика — что попадает в
 * историю, что нормализуется, что вычищается — живёт в `model/skillMapParams`
 * и проверяется тестами без DOM.
 *
 * 🔴 `push` только у выбора узла и фокуса. ТЗ требует, чтобы «назад»
 * возвращало предыдущий выбранный навык; всё остальное — глубина, режим,
 * связи, поиск — заменяет текущую запись. Иначе «назад» протаскивает человека
 * через двенадцать нажатий на панели, прежде чем увести с экрана.
 */
/**
 * Цепочка навыков, по которой человек пришёл к текущему, — слаги в порядке
 * посещения. Живёт в СОСТОЯНИИ ЗАПИСИ ИСТОРИИ, а не в `useState` компонента.
 *
 * 🔴 Это не мелочь реализации. Переход по чипу внутри паспорта — это `push` в
 * историю, поэтому «назад» браузера возвращает предыдущий навык МИМО любого
 * состояния компонента. Стек в `useState` при этом остаётся от другой ветки:
 * прошёл Kubernetes → Docker → Linux, вернулся кнопкой на Docker, нажал «назад»
 * браузера — и на Linux кнопка предлагала вернуться к Kubernetes, хотя пришли
 * с Docker. В состоянии записи стек восстанавливается вместе с самой записью,
 * и рассинхронизации не бывает по построению.
 */
interface ISkillMapHistoryState {
  skillTrail?: string[];
}

export const useSkillMapParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const params = useMemo(() => readSkillMapParams(search), [search]);
  const trail = useMemo(
    () => (location.state as ISkillMapHistoryState | null)?.skillTrail ?? [],
    [location.state],
  );

  const update = useCallback(
    (patch: Partial<ISkillMapParams>, skillTrail?: string[]) => {
      const next = nextSkillMapSearch(search, patch).toString();
      const to = `${location.pathname}${next ? `?${next}` : ''}${location.hash}`;
      navigate(to, {
        replace: !isHistoryPush(patch),
        // Цепочка едет вместе с записью. `undefined` — «оставить как было»
        // (например, при смене фильтра), пустой массив — «цепочка кончилась».
        state: skillTrail === undefined ? location.state : { skillTrail },
      });
    },
    [search, location.pathname, location.hash, location.state, navigate],
  );

  /** Шаг назад по цепочке — тот же, что и «назад» браузера, а не второй. */
  const goBack = useCallback(() => navigate(-1), [navigate]);

  return { params, update, trail, goBack };
};
